import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  Users, Plus, Edit2, Trash2, Search, Shield, X, Save,
  Mail, Lock, User, AlertCircle, CheckCircle, Eye, Pencil, Check, Ban
} from 'lucide-react';

const ROLES = [
  { value: 'Admin', label: 'Admin', description: 'Full system access', color: 'purple' },
  { value: 'DataEntry', label: 'Data Entry', description: 'Custom page access', color: 'blue' },
  { value: 'Manager', label: 'Manager', description: 'Full access with reporting', color: 'green' },
  { value: 'Auditor', label: 'Auditor', description: 'Scoped read-only access', color: 'amber' },
  { value: 'Employee', label: 'Employee', description: 'View-only access', color: 'gray' },
];

const AUDITOR_HR_PAGES = [
  '/hr/master-roll', '/hr/salary', '/hr/leave', '/hr/calendar',
  '/hr/org-structure', '/hr/grievances'
];
const EMPLOYEE_SELF_SERVICE_PAGES = ['/hr/leave', '/hr/calendar', '/hr/grievances'];

// Access levels
const ACCESS_LEVELS = [
  { value: 'none', label: 'No Access', icon: Ban, color: 'gray' },
  { value: 'view', label: 'View Only', icon: Eye, color: 'blue' },
  { value: 'edit', label: 'View & Edit', icon: Pencil, color: 'green' },
  { value: 'full', label: 'Edit & Delete', icon: Trash2, color: 'red' },
];

// ALL available pages grouped by category - COMPLETE LIST matching Layout.js
export const ALL_PAGES = [
  { category: "Dashboard", pages: [
    { path: "/", label: "Dashboard Overview" },
  ]},
  { category: "Production", pages: [
    { path: "/production/data-entry", label: "Data Entry (Excel)" },
    { path: "/production/furnace-dashboard", label: "Furnace Dashboard" },
    { path: "/production/report", label: "Production Report" },
    { path: "/production/duty-roster", label: "Duty Roster" },
    { path: "/production/material-recovery", label: "Material Recovery" },
    { path: "/production/crucible-report", label: "Crucible Report" },
    { path: "/production/production-plan", label: "Production Plan" },
    { path: "/production/batch-plan", label: "Batch Plan" },
  ]},
  { category: "Raw Material", pages: [
    { path: "/raw-material/stock", label: "Aluminium Scrap" },
    { path: "/raw-material/ferrous-scrap", label: "Ferrous Scrap" },
    { path: "/raw-material/reds-heavies", label: "Reds & Heavies" },
    { path: "/raw-material/dross-stock", label: "Dross" },
    { path: "/raw-material/processing-machines", label: "Processing Machines" },
    { path: "/raw-material/processing-plan", label: "Daily Processing Plan" },
    { path: "/raw-material/duty-roster", label: "Duty Roster" },
  ]},
  { category: "Dispatch", pages: [
    { path: "/dispatch/records", label: "Dispatch Records" },
    { path: "/dispatch/finished-goods", label: "Finished Goods Stock" },
    { path: "/dispatch/customer-orders", label: "Customer Orders" },
    { path: "/dispatch/orders", label: "Order Details" },
    { path: "/dispatch/delivery", label: "PO vs Delivery" },
    { path: "/dispatch/weighment", label: "Weighment Details" },
    { path: "/dispatch/duty-roster", label: "Duty Roster" },
  ]},
  { category: "Stores", pages: [
    { path: "/stores/consumable-stock", label: "Consumable Stock" },
    { path: "/stores/service-consumption", label: "Service Consumption" },
    { path: "/stores/grn", label: "GRN" },
    { path: "/stores/qa", label: "QA" },
    { path: "/stores/rejections", label: "Rejections" },
    { path: "/stores/material-requisitions", label: "Material Requisitions" },
    { path: "/stores/purchase-order", label: "Purchase Order" },
    { path: "/stores/payments", label: "Payments" },
    { path: "/stores/po-vs-delivered", label: "PO vs Delivered" },
    { path: "/stores/tenders", label: "Tenders & Enquiries" },
  ]},
  { category: "Projects", pages: [
    { path: "/projects/ongoing", label: "Ongoing Projects" },
    { path: "/projects/completed", label: "Projects Completed" },
    { path: "/projects/upcoming", label: "Upcoming Projects" },
    { path: "/projects/purchase-orders", label: "Project Purchase Orders" },
    { path: "/projects/grn", label: "Project GRN" },
    { path: "/projects/qa", label: "Project QA" },
    { path: "/projects/material-consumption", label: "Project Material Consumption" },
    { path: "/projects/service-consumption", label: "Project Service Consumption" },
    { path: "/projects/payments", label: "Project Payments" },
  ]},
  { category: "Maintenance", pages: [
    { path: "/maintenance/machinery", label: "Plant & Machinery" },
    { path: "/maintenance/log", label: "Maintenance Log" },
    { path: "/maintenance/shutdown", label: "Report a Shutdown" },
    { path: "/maintenance/duty-roster", label: "Duty Roster" },
    { path: "/maintenance/drawings", label: "Drawings" },
  ]},
  { category: "HR", pages: [
    { path: "/hr/master-roll", label: "Employment Directory" },
    { path: "/hr/salary", label: "Master Roll" },
    { path: "/hr/leave", label: "Leave Management" },
    { path: "/hr/calendar", label: "Holiday Calendar" },
    { path: "/hr/org-structure", label: "Organisation Structure" },
    { path: "/hr/grievances", label: "Grievances" },
  ]},
  { category: "Imports", pages: [
    { path: "/imports/supplier-dashboard", label: "Supplier Dashboard" },
    { path: "/imports/purchase-order", label: "Purchase Order" },
    { path: "/imports/grn", label: "GRN (Goods Receipt)" },
    { path: "/imports/sales-contract", label: "Sales Contract Directory" },
    { path: "/imports/po-vs-delivered", label: "PO vs Delivered" },
    { path: "/imports/shipments", label: "Shipment Tracking" },
    { path: "/imports/claims", label: "Claims" },
    { path: "/imports/claims-dashboard", label: "Claims Dashboard" },
    { path: "/imports/documents", label: "Documents" },
  ]},
  { category: "Master Data", pages: [
    { path: "/master-data/production", label: "Production" },
    { path: "/master-data/raw-material", label: "Raw Material" },
    { path: "/master-data/dispatch", label: "Dispatch" },
    { path: "/master-data/stores", label: "Stores" },
    { path: "/master-data/projects", label: "Projects" },
    { path: "/master-data/maintenance", label: "Maintenance" },
    { path: "/master-data/hr", label: "HR" },
    { path: "/master-data/imports", label: "Imports" },
  ]},
  { category: "Settings", pages: [
    { path: "/settings", label: "General Settings" },
    { path: "/settings/users", label: "User Management" },
    { path: "/settings/data-integrity", label: "Data Integrity Dashboard" },
    { path: "/camera", label: "Document Camera" },
  ]},
];

// Default access for Data Entry role - common pages with edit access
const DATA_ENTRY_DEFAULT_ACCESS = {
  "/production/data-entry": "edit",
  "/production/report": "view",
  "/dispatch/delivery": "view",
  "/dispatch/orders": "view",
  "/stores/grn": "edit",
  "/stores/qa": "edit",
  "/stores/purchase-order": "edit",
  "/imports/purchase-order": "edit",
  "/imports/grn": "edit",
  "/imports/sales-contract": "edit",
  "/master-data/stores": "edit",
  "/master-data/raw-material": "edit",
};

function UserManagement() {
  const { API, user: currentUser } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false); // Flag to track if we're editing
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'DataEntry',
    siteId: '',
    department: '',
    pageAccess: { ...DATA_ENTRY_DEFAULT_ACCESS }
  });
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchUsers();
    // Expand all categories by default
    const expanded = {};
    ALL_PAGES.forEach(cat => { expanded[cat.category] = true; });
    setExpandedCategories(expanded);
  }, []);

  // Update page access when role changes - ONLY for new users, not when editing
  useEffect(() => {
    // Skip if we're in edit mode - preserve existing permissions
    if (isEditMode) return;
    
    if (formData.role === 'Admin' || formData.role === 'Manager') {
      // Full access to all pages
      const fullAccess = {};
      ALL_PAGES.forEach(cat => {
        cat.pages.forEach(page => {
          fullAccess[page.path] = 'edit';
        });
      });
      setFormData(prev => ({ ...prev, pageAccess: fullAccess }));
    } else if (formData.role === 'DataEntry') {
      setFormData(prev => ({ ...prev, pageAccess: { ...DATA_ENTRY_DEFAULT_ACCESS } }));
    } else if (formData.role === 'Auditor') {
      setFormData(prev => ({ ...prev, pageAccess: Object.fromEntries(AUDITOR_HR_PAGES.map(path => [path, 'view'])) }));
    } else {
      setFormData(prev => ({ ...prev, pageAccess: Object.fromEntries(EMPLOYEE_SELF_SERVICE_PAGES.map(path => [path, 'view'])) }));
    }
  }, [formData.role, isEditMode]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ type: 'error', text: 'Failed to load users. Admin access required.' });
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        siteId: formData.siteId,
        department: formData.department,
        page_access: formData.pageAccess  // Send custom page access
      };
      
      if (editUser) {
        await axios.put(`${API}/auth/users/${editUser.id}`, payload);
        setMessage({ type: 'success', text: 'User updated successfully!' });
      } else {
        await axios.post(`${API}/auth/users`, payload);
        setMessage({ type: 'success', text: 'User created successfully! Share the credentials with them.' });
      }
      fetchUsers();
      handleCloseModal();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Operation failed' });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/auth/users/${userId}`);
      setMessage({ type: 'success', text: 'User deleted successfully' });
      fetchUsers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete user' });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await axios.put(`${API}/auth/users/${user.id}`, { status: newStatus });
      setMessage({ type: 'success', text: `User ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully` });
      fetchUsers();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update user status' });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditUser(user);
      setIsEditMode(true); // Set edit mode BEFORE setting form data
      // Load existing page_access from user or use default based on role
      const existingPageAccess = user.page_access || 
        (user.role === 'DataEntry' ? { ...DATA_ENTRY_DEFAULT_ACCESS } : 
         (user.role === 'Admin' || user.role === 'Manager') ? (() => {
           const fullAccess = {};
           ALL_PAGES.forEach(cat => {
             cat.pages.forEach(page => { fullAccess[page.path] = 'edit'; });
           });
           return fullAccess;
         })() : {});
      
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        siteId: user.siteId || '',
        department: user.department || '',
        pageAccess: existingPageAccess
      });
    } else {
      setEditUser(null);
      setIsEditMode(false); // Not in edit mode for new users
      setFormData({ 
        name: '', 
        email: '', 
        password: '', 
        role: 'DataEntry', 
        siteId: '',
        department: '',
        pageAccess: { ...DATA_ENTRY_DEFAULT_ACCESS } 
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditUser(null);
    setIsEditMode(false); // Reset edit mode
    setFormData({ name: '', email: '', password: '', role: 'DataEntry', siteId: '', department: '', pageAccess: { ...DATA_ENTRY_DEFAULT_ACCESS } });
  };

  const setPageAccess = (pagePath, accessLevel) => {
    if (['Admin', 'Manager', 'Auditor'].includes(formData.role)) return;
    
    setFormData(prev => {
      const newAccess = { ...prev.pageAccess };
      if (accessLevel === 'none') {
        delete newAccess[pagePath];
      } else {
        newAccess[pagePath] = accessLevel;
      }
      return { ...prev, pageAccess: newAccess };
    });
  };

  const getPageAccess = (pagePath) => {
    if (formData.role === 'Admin' || formData.role === 'Manager') return 'edit';
    if (formData.role === 'Auditor') return AUDITOR_HR_PAGES.includes(pagePath) ? 'view' : 'none';
    return formData.pageAccess[pagePath] || 'none';
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const setCategoryAccess = (category, accessLevel) => {
    if (['Admin', 'Manager', 'Auditor'].includes(formData.role)) return;
    
    const catPages = ALL_PAGES.find(c => c.category === category)?.pages || [];
    setFormData(prev => {
      const newAccess = { ...prev.pageAccess };
      catPages.forEach(page => {
        if (accessLevel === 'none') {
          delete newAccess[page.path];
        } else {
          newAccess[page.path] = accessLevel;
        }
      });
      return { ...prev, pageAccess: newAccess };
    });
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count pages with access
  const accessCount = Object.keys(formData.pageAccess).length;
  const totalPages = ALL_PAGES.reduce((sum, cat) => sum + cat.pages.length, 0);

  if (currentUser?.role !== 'Admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="mx-auto text-red-400 mb-4" size={64} />
          <h2 className="text-xl font-bold text-gray-800">Access Denied</h2>
          <p className="text-gray-600 mt-2">Admin access required to manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="text-purple-600" size={28} />
            User Management
          </h1>
          <p className="text-gray-600 mt-1">Create and manage user accounts with granular page-level access control</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition shadow-md"
          data-testid="add-user-btn"
        >
          <Plus size={18} />
          Add New User
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Total Users</p>
          <p className="text-2xl font-bold text-gray-900">{users.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Admins</p>
          <p className="text-2xl font-bold text-purple-600">{users.filter(u => u.role === 'Admin').length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Data Entry</p>
          <p className="text-2xl font-bold text-blue-600">{users.filter(u => u.role === 'DataEntry').length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{users.filter(u => u.status === 'Active').length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="relative max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-100 focus:border-purple-400"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${
                        user.role === 'Admin' ? 'bg-purple-500' :
                        user.role === 'DataEntry' ? 'bg-blue-500' :
                        user.role === 'Manager' ? 'bg-green-500' :
                        user.role === 'Auditor' ? 'bg-amber-500' : 'bg-gray-500'
                      }`}>
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">ID: {user.id?.slice(0, 8)}...</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      user.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                      user.role === 'DataEntry' ? 'bg-blue-100 text-blue-700' :
                      user.role === 'Manager' ? 'bg-green-100 text-green-700' :
                      user.role === 'Auditor' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {user.role === 'DataEntry' ? 'Data Entry' : user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleToggleStatus(user)}
                      disabled={user.id === currentUser?.id}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${
                        user.status === 'Active' 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      } ${user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {user.status}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleOpenModal(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="Edit User"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        disabled={user.id === currentUser?.id}
                        className={`p-2 text-red-600 hover:bg-red-50 rounded-lg transition ${
                          user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title="Delete User"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-4">
            <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{editUser ? 'Edit User' : 'Add New User'}</h2>
                <p className="text-sm opacity-80 mt-1">Configure user details and page-level access permissions</p>
              </div>
              <button onClick={handleCloseModal} className="p-2 hover:bg-white/20 rounded-lg transition">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto">
              <div className="p-5 space-y-5">
                {/* Basic Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User size={14} className="inline mr-1" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Mail size={14} className="inline mr-1" />
                      Email *
                      {editUser && (
                        <span className="ml-2 text-xs text-blue-600 font-normal">(Admin can change)</span>
                      )}
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                      placeholder="Email address"
                      data-testid="user-email-input"
                    />
                    {editUser && (
                      <p className="text-xs text-gray-500 mt-1">
                        Changing email will require the user to login with the new email
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Lock size={14} className="inline mr-1" />
                      Password {editUser && '(optional)'}
                    </label>
                    <input
                      type="password"
                      required={!editUser}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                      placeholder={editUser ? '••••••••' : 'Password'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Shield size={14} className="inline mr-1" />
                      Role *
                    </label>
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                    >
                      {ROLES.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">HR Site Scope</label>
                    <input
                      type="text"
                      value={formData.siteId}
                      onChange={(e) => setFormData({ ...formData, siteId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                      placeholder="Exact site identifier"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">HR Department Scope</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                      placeholder="Exact department name"
                    />
                  </div>
                  <p className="md:col-span-2 text-xs text-gray-500">
                    Site and department are required for scoped HR access. Admin access is not scoped.
                  </p>
                </div>

                {/* Access Summary */}
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center gap-3">
                    <Shield size={20} className="text-purple-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Page Access Configuration</p>
                      <p className="text-xs text-gray-500">
                        {formData.role === 'Admin' || formData.role === 'Manager'
                          ? 'Full access to all pages (Edit & Delete)'
                          : formData.role === 'Auditor'
                            ? 'Read-only HR access within assigned scope'
                          : `${accessCount} of ${totalPages} pages enabled`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="flex items-center gap-1 px-2 py-1 bg-gray-200 rounded"><Ban size={12} /> None</span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded"><Eye size={12} /> View</span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded"><Pencil size={12} /> Edit</span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded"><Trash2 size={12} /> Edit & Delete</span>
                  </div>
                </div>

                {/* Page Access Grid */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {ALL_PAGES.map(category => {
                    const isExpanded = expandedCategories[category.category];
                    const categoryAccess = category.pages.filter(p => formData.pageAccess[p.path]).length;
                    
                    return (
                      <div key={category.category} className="border-b border-gray-200 last:border-b-0">
                        {/* Category Header */}
                        <div 
                          className="flex items-center justify-between px-4 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
                          onClick={() => toggleCategory(category.category)}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`transform transition ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                            <span className="font-semibold text-gray-800">{category.category}</span>
                            <span className="text-xs text-gray-500">({categoryAccess}/{category.pages.length} enabled)</span>
                          </div>
                          {!['Admin', 'Manager', 'Auditor'].includes(formData.role) && (
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => setCategoryAccess(category.category, 'none')}
                                className="px-2 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 transition"
                              >
                                None
                              </button>
                              <button
                                type="button"
                                onClick={() => setCategoryAccess(category.category, 'view')}
                                className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition"
                              >
                                View All
                              </button>
                              <button
                                type="button"
                                onClick={() => setCategoryAccess(category.category, 'edit')}
                                className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200 transition"
                              >
                                Edit All
                              </button>
                              <button
                                type="button"
                                onClick={() => setCategoryAccess(category.category, 'full')}
                                className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 transition"
                              >
                                Full All
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Category Pages */}
                        {isExpanded && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 p-2 bg-white">
                            {category.pages.map(page => {
                              const access = getPageAccess(page.path);
                              const isAdmin = ['Admin', 'Manager', 'Auditor'].includes(formData.role);
                              
                              return (
                                <div 
                                  key={page.path} 
                                  className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${
                                    access === 'none' ? 'bg-gray-50 border-gray-200 text-gray-500' :
                                    access === 'view' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                                    access === 'edit' ? 'bg-green-50 border-green-200 text-green-700' :
                                    'bg-red-50 border-red-200 text-red-700'
                                  }`}
                                >
                                  <span className="truncate flex-1">{page.label}</span>
                                  {!isAdmin && (
                                    <select
                                      value={access}
                                      onChange={(e) => setPageAccess(page.path, e.target.value)}
                                      className={`ml-2 px-2 py-1 text-xs rounded border-0 outline-none cursor-pointer ${
                                        access === 'none' ? 'bg-gray-200' :
                                        access === 'view' ? 'bg-blue-200' :
                                        access === 'edit' ? 'bg-green-200' :
                                        'bg-red-200'
                                      }`}
                                    >
                                      <option value="none">None</option>
                                      <option value="view">View</option>
                                      <option value="edit">Edit</option>
                                      <option value="full">Edit & Delete</option>
                                    </select>
                                  )}
                                  {isAdmin && (
                                    <span className="ml-2 flex items-center gap-1 text-xs bg-red-200 text-red-700 px-2 py-0.5 rounded">
                                      <Trash2 size={12} /> Full
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 flex items-center gap-2 font-medium"
                >
                  <Save size={16} />
                  {editUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
